// =============================================
// notesMedia — client side of note image/video upload + resolution
// =============================================
// Upload goes to the validated /api/kv/media endpoint; bytes are resolved back
// by id via an AUTHENTICATED fetch → object URL (so no token ever lands in the
// saved doc or a <img>/<video> src). Falls back to localStorage (kv_media row +
// data URL) when the API/token isn't available, matching the rest of the app.
// =============================================
import { getTableData, addRow as dbAddRow, saveRow as dbSaveRow, checkApi } from '../db-viewer/data/dataAccess';
import { getCurrentUserId } from './notesLib';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const MEDIA_LIMITS: Record<string, number> = { image: 5 * 1024 * 1024, video: 25 * 1024 * 1024 };
export const ACCEPTED_MIME = 'image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,video/ogg,video/quicktime';

export interface MediaMeta {
  media_id: number;
  note_id: number;
  kind: 'image' | 'video' | 'drawing';
  mime: string;
  size_bytes: number;
  original_name?: string;
}

function token(): string | null { return localStorage.getItem('akt_auth_token'); }
async function apiReady(): Promise<boolean> { return !!token() && (await checkApi()); }

export function kindForMime(mime: string): 'image' | 'video' | null {
  if (mime.startsWith('image/') && mime !== 'image/svg+xml') return 'image';
  if (mime.startsWith('video/')) return 'video';
  return null;
}

/** Strip the `data:...;base64,` prefix from a FileReader result. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result); resolve(s.slice(s.indexOf(',') + 1)); };
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(blob);
  });
}
const fileToBase64 = blobToBase64;

/** Validate + upload one file to a note. Returns the media metadata (no bytes). */
export async function uploadMedia(noteId: number, file: File): Promise<MediaMeta> {
  const kind = kindForMime(file.type);
  if (!kind) throw new Error('Only images (PNG/JPEG/GIF/WebP) and videos (MP4/WebM/OGG/MOV) are allowed');
  if (file.size > MEDIA_LIMITS[kind]) {
    throw new Error(`${kind === 'image' ? 'Image' : 'Video'} too large — max ${Math.round(MEDIA_LIMITS[kind] / 1048576)} MB`);
  }
  const b64 = await fileToBase64(file);

  if (await apiReady()) {
    const res = await fetch(`${API_BASE}/kv/media`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ note_id: noteId, mime: file.type, file_base64: b64, original_name: file.name }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({} as any)); throw new Error(e.message || `Upload failed (${res.status})`); }
    return await res.json();
  }

  // localStorage fallback (offline / no token) — keep small images working.
  const row = await dbAddRow('kv_media', {
    note_id: noteId, user_id: getCurrentUserId(), kind, mime: file.type,
    file_base64: b64, size_bytes: file.size, original_name: file.name, is_active: 1, is_deleted: 0,
  }, 'media_id');
  return { media_id: row.media_id, note_id: noteId, kind, mime: file.type, size_bytes: file.size, original_name: file.name };
}

// Resolved src cache: media_id → object/data URL (kept for the page session).
const srcCache = new Map<number, string>();

/** Resolve a media id to a usable <img>/<video> src (cached). */
export async function getMediaSrc(mediaId: number): Promise<string> {
  if (srcCache.has(mediaId)) return srcCache.get(mediaId)!;
  if (await apiReady()) {
    const res = await fetch(`${API_BASE}/kv/media/${mediaId}`, { headers: { Authorization: `Bearer ${token()}` } });
    if (!res.ok) throw new Error(`Media ${mediaId} failed to load`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    srcCache.set(mediaId, url);
    return url;
  }
  // localStorage: build a data URL from the cached row.
  const row = getTableData('kv_media').find((r: any) => r.media_id == mediaId); // eslint-disable-line eqeqeq
  if (!row || !row.file_base64) throw new Error('Media not available offline');
  const url = `data:${row.mime || 'application/octet-stream'};base64,${row.file_base64}`;
  srcCache.set(mediaId, url);
  return url;
}

/** Soft-delete a media row + drop its cached URL. Best-effort. */
export async function deleteMedia(mediaId: number): Promise<void> {
  const cached = srcCache.get(mediaId);
  if (cached && cached.startsWith('blob:')) URL.revokeObjectURL(cached);
  srcCache.delete(mediaId);
  if (await apiReady()) {
    await fetch(`${API_BASE}/kv/media/${mediaId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }).catch(() => {});
    return;
  }
  await dbSaveRow('kv_media', mediaId, { is_deleted: 1, is_active: 0 }, 'media_id').catch(() => {});
}

/** Drop a cached resolved URL so the next getMediaSrc refetches (after a re-edit). */
export function invalidateMediaSrc(mediaId: number): void {
  const u = srcCache.get(mediaId);
  if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
  srcCache.delete(mediaId);
}

// ---- Drawings: a kind='drawing' media with a rendered PNG + editable scene_json ----

/** Create a drawing on a note: stores the PNG (display) + the vector scene (re-edit). */
export async function uploadDrawing(noteId: number, sceneJson: any, pngBlob: Blob): Promise<MediaMeta> {
  const png = await blobToBase64(pngBlob);
  const scene = JSON.stringify(sceneJson || {});
  if (await apiReady()) {
    const res = await fetch(`${API_BASE}/kv/drawing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ note_id: noteId, png_base64: png, scene_json: scene }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({} as any)); throw new Error(e.message || `Save failed (${res.status})`); }
    return await res.json();
  }
  const row = await dbAddRow('kv_media', {
    note_id: noteId, user_id: getCurrentUserId(), kind: 'drawing', mime: 'image/png',
    file_base64: png, scene_json: scene, size_bytes: Math.round(png.length * 0.75),
    original_name: 'drawing.png', is_active: 1, is_deleted: 0,
  }, 'media_id');
  return { media_id: row.media_id, note_id: noteId, kind: 'drawing', mime: 'image/png', size_bytes: row.size_bytes || 0, original_name: 'drawing.png' };
}

/** Update an existing drawing (new strokes + new PNG) + invalidate its cached src. */
export async function updateDrawing(mediaId: number, sceneJson: any, pngBlob: Blob): Promise<void> {
  const png = await blobToBase64(pngBlob);
  const scene = JSON.stringify(sceneJson || {});
  if (await apiReady()) {
    const res = await fetch(`${API_BASE}/kv/drawing/${mediaId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ png_base64: png, scene_json: scene }),
    });
    if (!res.ok) { const e = await res.json().catch(() => ({} as any)); throw new Error(e.message || `Save failed (${res.status})`); }
  } else {
    await dbSaveRow('kv_media', mediaId, { file_base64: png, scene_json: scene, size_bytes: Math.round(png.length * 0.75) }, 'media_id');
  }
  invalidateMediaSrc(mediaId);
}

/** Fetch a drawing's vector scene for reopen-and-edit (null if none). */
export async function getDrawingScene(mediaId: number): Promise<any> {
  if (await apiReady()) {
    const res = await fetch(`${API_BASE}/kv/drawing/${mediaId}`, { headers: { Authorization: `Bearer ${token()}` } });
    if (res.ok) { const j = await res.json(); try { return j.scene_json ? JSON.parse(j.scene_json) : null; } catch { return null; } }
    return null;
  }
  const row = getTableData('kv_media').find((r: any) => r.media_id == mediaId); // eslint-disable-line eqeqeq
  if (row && row.scene_json) { try { return JSON.parse(row.scene_json); } catch { return null; } }
  return null;
}

/** All of the caller's media metadata (no bytes) — for dashboard space totals. */
export async function loadMediaMeta(): Promise<MediaMeta[]> {
  if (await apiReady()) {
    try {
      const res = await fetch(`${API_BASE}/kv/media`, { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) return await res.json();
    } catch { /* fall through to cache */ }
  }
  const me = getCurrentUserId();
  return getTableData('kv_media')
    .filter((r: any) => r.is_deleted != 1 && (me === 0 || r.user_id == me)) // eslint-disable-line eqeqeq
    .map((r: any) => ({
      media_id: r.media_id, note_id: r.note_id, kind: r.kind, mime: r.mime,
      size_bytes: Number(r.size_bytes) || 0, original_name: r.original_name,
    }));
}
